import { useState, useEffect, useCallback, useRef } from "react";
import { useTranslation } from "react-i18next";
import { api, formatApiErrorDetail } from "../lib/api";
import { toast } from "sonner";
import { ChevronDown, Check, Save, Trash2, Pencil, Plus, ArrowUp, ArrowDown, Settings, Columns as ColumnsIcon, SlidersHorizontal, LayoutList, Share2, Users } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { Checkbox } from "./ui/checkbox";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { ALL_PRODUCT_COLUMNS, DEFAULT_VISIBLE_COLUMNS } from "./ProductTable";
import { ALL_FILTER_KEYS, FILTER_LABELS } from "./FilterBar";
import { PRESET_FIELDS, applyPresetsToFilters, emptyPreset } from "../lib/presets";

const CURRENT_VIEW_STORAGE_KEY = "productsCurrentViewId";

// iter 201 (2026-02): 搜索框可覆盖的字段目录。每个 View 在编辑器里勾选
// 一个子集,搜索时后端只 OR-匹配这些字段。空数组 → 用 DEFAULT_Q_FIELDS。
export const Q_FIELD_CATALOG = [
  { id: "contract_no",         label: "Contract No · 合同号" },
  { id: "budget_no",           label: "Budget No · 预算号" },
  { id: "description",         label: "Description · 产品描述" },
  { id: "customer_product_no", label: "Customer Product No · 客户产品编号" },
  { id: "gt_sku",              label: "GT SKU" },
  { id: "item_no",             label: "Item No · 公司产品编号" },
  { id: "invoice_no",          label: "Invoice No · 发票号" },
  { id: "po_number",           label: "PO Number · 客户 PO 号" },
  // iter 216 (2026-02): UPC 智能搜索 — 支持相机扫码填的完整 UPC 或前缀。
  { id: "upc",                 label: "UPC / Barcode · 条码" },
];
// iter 272 (2026-06): 默认 4 个字段,与后端 DEFAULT_Q_FIELDS 一致。
// iter 276: system view names are stored in English; display localized.
const SYSTEM_VIEW_LABEL_KEYS = { Default: "views.sys_default", Compact: "views.sys_compact", Full: "views.sys_full", "Sample Warehouse": "views.sys_sample_warehouse" };
export function useViewLabel() {
  const { t } = useTranslation();
  return (v) => (v?.is_system && SYSTEM_VIEW_LABEL_KEYS[v.name] ? t(SYSTEM_VIEW_LABEL_KEYS[v.name], v.name) : v?.name);
}

export const DEFAULT_Q_FIELDS = ["item_no", "customer_product_no", "description", "contract_no"];

export function useProductViews({ filters, setFilters, emptyFilters, sortDir, setSortDir, priceCurrency, setPriceCurrency }) {
  const [views, setViews] = useState([]);
  const [currentViewId, setCurrentViewId] = useState(() => localStorage.getItem(CURRENT_VIEW_STORAGE_KEY) || null);
  const [visibleColumns, setVisibleColumns] = useState(DEFAULT_VISIBLE_COLUMNS);
  const [columnWidths, setColumnWidths] = useState({});
  const [visibleFilters, setVisibleFilters] = useState([]); // [] = all filters visible (legacy views)
  const [detailView, setDetailView] = useState("pooled"); // "pooled" | "order"
  const [lockedFields, setLockedFields] = useState([]);
  const [presets, setPresets] = useState({}); // { field_id: { mode, value, value2 } }
  const [qFields, setQFields] = useState(DEFAULT_Q_FIELDS);
  const applyingRef = useRef(false);

  const persistCurrent = useCallback((id) => {
    setCurrentViewId(id);
    if (id) localStorage.setItem(CURRENT_VIEW_STORAGE_KEY, id);
    else localStorage.removeItem(CURRENT_VIEW_STORAGE_KEY);
  }, []);

  const applyView = useCallback((view) => {
    if (!view) return;
    applyingRef.current = true;
    setVisibleColumns(view.columns && view.columns.length ? view.columns : DEFAULT_VISIBLE_COLUMNS);
    setColumnWidths(view.column_widths || {});
    setVisibleFilters(Array.isArray(view.visible_filters) ? view.visible_filters : []);
    setDetailView(view.detail_view === "order" ? "order" : "pooled");
    // iter 200 (2026-02): Views 只承载 Preset 里锁定的过滤字段, 不再恢复
    // 上次会话里手动填过的临时过滤值。旧行为 (iter 87) 会把 view.filters 里
    // 所有非空字段 (哪怕不是 preset) 都当"启动过滤"重新套上,导致用户下次
    // 打开产品清单看到空表,得手动 Reset。现在:先 reset 到 emptyFilters,只
    // 用 _presets 中锁定的字段覆盖上去。会话内新填的过滤条件是临时的,不
    // 会污染下一次页面加载。
    const rawFilters = view.filters || {};
    const { _presets } = rawFilters;
    setPresets(_presets && typeof _presets === "object" ? _presets : {});
    // iter 201: q_fields lives on the View top-level. Legacy views (missing
    // q_fields) fall back to DEFAULT_Q_FIELDS so the search box behaves
    // consistently across old and new views.
    const validCatalog = new Set(Q_FIELD_CATALOG.map((f) => f.id));
    const savedQFields = Array.isArray(view.q_fields) ? view.q_fields.filter((f) => validCatalog.has(f)) : [];
    setQFields(savedQFields.length ? savedQFields : DEFAULT_Q_FIELDS);
    const base = { ...(emptyFilters || {}) };
    const { filters: nextFilters, lockedFields: nextLocked } = applyPresetsToFilters(base, _presets);
    setFilters(nextFilters);
    setLockedFields(nextLocked);
    if (view.sort?.direction) setSortDir(view.sort.direction);
    if (view.currency && (view.currency === "USD" || view.currency === "CAD")) setPriceCurrency(view.currency);
    persistCurrent(view.id);
    // release the applying flag after the state settles so dirty tracking resumes
    setTimeout(() => { applyingRef.current = false; }, 50);
  }, [setFilters, emptyFilters, setSortDir, setPriceCurrency, persistCurrent]);

  const loadViews = useCallback(async () => {
    try {
      const { data } = await api.get("/product-views");
      setViews(data.items || []);
      const stored = localStorage.getItem(CURRENT_VIEW_STORAGE_KEY);
      const initial = (data.items || []).find((v) => v.id === stored) || (data.items || []).find((v) => v.name === "Default");
      if (initial) applyView(initial);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to load views");
    }
  }, [applyView]);

  useEffect(() => { loadViews(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, []);

  const currentView = views.find((v) => v.id === currentViewId) || null;

  const { user } = useAuth();
  const isAdmin = user?.role === "admin";
  // iter 250: shared views are read-only for everyone except owner / admin.
  const canEditView = useCallback((v) => !!v && !v.is_system && (v.user_id === user?.id || (isAdmin && v.is_shared)), [user, isAdmin]);

  const createView = useCallback(async (name, isShared = false) => {
    try {
      // iter 200 (2026-02): Views 只存 Preset 中锁定的过滤字段, 不再把会话
      // 里临时填的过滤条件带进 View。用户想固化某个过滤,必须在 Preset 编辑
      // 里显式加它。这样任何 View 的"启动条件"永远只有 Preset,不会因为
      // 用户在会话中筛选而变。
      const filtersOnlyPresets = { _presets: presets || {} };
      const { data } = await api.post("/product-views", {
        name,
        columns: visibleColumns,
        column_widths: columnWidths,
        filters: filtersOnlyPresets,
        visible_filters: visibleFilters,
        q_fields: qFields,
        detail_view: detailView,
        sort: { direction: sortDir },
        currency: priceCurrency,
        is_shared: !!isShared,
      });
      const next = [...views, data];
      setViews(next);
      // iter 87: re-apply the freshly-saved view so its _presets take effect
      // (lock the toolbar fields, overlay preset values). Without this, the
      // toolbar shows stale state until the user manually switches views.
      applyView(data);
      toast.success(`View "${name}" saved`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to save view");
    }
  }, [visibleColumns, columnWidths, presets, visibleFilters, qFields, detailView, sortDir, priceCurrency, views, applyView]);

  const updateCurrentView = useCallback(async () => {
    if (!canEditView(currentView)) return;
    try {
      // iter 200: same policy as createView — Views only persist _presets.
      const filtersOnlyPresets = { _presets: presets || {} };
      const { data } = await api.put(`/product-views/${currentView.id}`, {
        name: currentView.name,
        columns: visibleColumns,
        column_widths: columnWidths,
        filters: filtersOnlyPresets,
        visible_filters: visibleFilters,
        q_fields: qFields,
        detail_view: detailView,
        sort: { direction: sortDir },
        currency: priceCurrency,
        is_shared: currentView.is_shared,
      });
      setViews((vs) => vs.map((v) => (v.id === data.id ? data : v)));
      // iter 87: re-apply so preset changes lock the toolbar immediately.
      applyView(data);
      toast.success(`View "${data.name}" updated`);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Failed to update view");
    }
  }, [currentView, canEditView, visibleColumns, columnWidths, presets, visibleFilters, qFields, detailView, sortDir, priceCurrency, applyView]);

  const putViewMeta = useCallback(async (view, patch, okMsg, failMsg) => {
    try {
      const { data } = await api.put(`/product-views/${view.id}`, {
        name: view.name,
        columns: view.columns,
        column_widths: view.column_widths,
        filters: view.filters,
        visible_filters: view.visible_filters,
        q_fields: view.q_fields,
        detail_view: view.detail_view,
        sort: view.sort,
        currency: view.currency,
        is_shared: view.is_shared,
        ...patch,
      });
      setViews((vs) => vs.map((v) => (v.id === data.id ? data : v)));
      toast.success(okMsg);
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || failMsg);
    }
  }, []);

  const renameView = useCallback((view, newName) => putViewMeta(view, { name: newName }, "View renamed", "Rename failed"), [putViewMeta]);
  // iter 250: admin-only toggle — share / unshare a view with all users.
  const toggleShareView = useCallback((view) => putViewMeta(
    view, { is_shared: !view.is_shared },
    view.is_shared ? "View is now private" : "View shared with all users", "Share failed",
  ), [putViewMeta]);

  const deleteView = useCallback(async (view) => {
    if (!window.confirm(`Delete view "${view.name}"?`)) return;
    try {
      await api.delete(`/product-views/${view.id}`);
      setViews((vs) => vs.filter((v) => v.id !== view.id));
      if (currentViewId === view.id) {
        const fallback = views.find((v) => v.name === "Default");
        if (fallback) applyView(fallback);
      }
      toast.success("View deleted");
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Delete failed");
    }
  }, [currentViewId, views, applyView]);

  const setColumnWidth = useCallback((id, width) => {
    setColumnWidths((w) => ({ ...w, [id]: width }));
  }, []);

  return {
    views, currentView, currentViewId, visibleColumns, columnWidths, visibleFilters, detailView,
    lockedFields, presets, setPresets,
    qFields, setQFields,
    setVisibleColumns, setColumnWidth, setVisibleFilters, setDetailView,
    applyView, createView, updateCurrentView, renameView, deleteView,
    toggleShareView, canEditView, isAdmin,
  };
}


export default function ProductViewsBar({
  views, currentView, visibleColumns, setVisibleColumns,
  visibleFilters, setVisibleFilters,
  detailView, setDetailView,
  presets, setPresets, categoryOptions,
  qFields, setQFields,
  applyView, createView, updateCurrentView, renameView, deleteView,
  toggleShareView, canEditView = (v) => v && !v.is_system, isAdmin = false,
}) {
  const { t } = useTranslation();
  const viewLabel = useViewLabel();
  const [saveShared, setSaveShared] = useState(false);
  const editable = canEditView(currentView);
  const [viewsOpen, setViewsOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [editTab, setEditTab] = useState("columns"); // "columns" | "filters" | "detail"
  const [saveDialogOpen, setSaveDialogOpen] = useState(false);
  const [renameTarget, setRenameTarget] = useState(null);
  const [newName, setNewName] = useState("");
  const wrapperRef = useRef(null);
  // Track that the user actually touched something inside the Edit dialog, so
  // we can auto-save exactly once when they close it (Q4-a: immediate apply,
  // autosave to current view). Reset each time the dialog opens.
  const dirtyRef = useRef(false);

  useEffect(() => {
    const onDoc = (e) => {
      if (wrapperRef.current && !wrapperRef.current.contains(e.target)) setViewsOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, []);

  const toggleCol = (id) => {
    dirtyRef.current = true;
    setVisibleColumns((cols) => cols.includes(id) ? cols.filter((c) => c !== id) : [...cols, id]);
  };

  const toggleFilter = (id) => {
    dirtyRef.current = true;
    setVisibleFilters((keys) => {
      const list = keys && keys.length ? [...keys] : [...ALL_FILTER_KEYS];
      const idx = list.indexOf(id);
      if (idx >= 0) list.splice(idx, 1); else list.push(id);
      return list;
    });
  };
  // iter 201: toggle a field in the search-box scope. Guards against
  // emptying the list (search would match nothing).
  const toggleQField = (id) => {
    dirtyRef.current = true;
    setQFields((keys) => {
      const list = Array.isArray(keys) && keys.length ? [...keys] : [...DEFAULT_Q_FIELDS];
      const idx = list.indexOf(id);
      if (idx >= 0) {
        if (list.length <= 1) {
          toast.error("Keep at least one searchable field");
          return list;
        }
        list.splice(idx, 1);
      } else {
        list.push(id);
      }
      return list;
    });
  };
  const activeFilterCount = (visibleFilters && visibleFilters.length) ? visibleFilters.length : ALL_FILTER_KEYS.length;

  const moveCol = (id, dir) => {
    dirtyRef.current = true;
    setVisibleColumns((cols) => {
      const idx = cols.indexOf(id);
      if (idx < 0) return cols;
      const target = idx + dir;
      if (target < 0 || target >= cols.length) return cols;
      const next = [...cols];
      [next[idx], next[target]] = [next[target], next[idx]];
      return next;
    });
  };

  const openSaveAs = () => { setNewName(""); setSaveShared(false); setSaveDialogOpen(true); };
  const confirmSaveAs = () => {
    const trimmed = newName.trim();
    if (!trimmed) { toast.error("Name required"); return; }
    createView(trimmed, saveShared);
    setSaveDialogOpen(false);
  };

  const openRename = (v) => { setRenameTarget(v); setNewName(v.name); };
  const confirmRename = () => {
    const trimmed = newName.trim();
    if (!trimmed) { toast.error("Name required"); return; }
    renameView(renameTarget, trimmed);
    setRenameTarget(null);
  };

  // Autosave-on-close policy for the unified Edit dialog. User views persist
  // their changes silently to the current view; System views show a hint
  // instead so the user knows to Save-As.
  const handleEditOpenChange = (open) => {
    setEditOpen(open);
    if (open) {
      dirtyRef.current = false;
      return;
    }
    if (!dirtyRef.current || !currentView) return;
    if (!editable) {
      toast.message('The "' + currentView.name + '" view is read-only. Use "Save as new View" to keep these changes.');
      return;
    }
    updateCurrentView();
  };

  return (
    <div className="flex items-center gap-2" data-testid="product-views-bar">
      <div ref={wrapperRef} className="relative">
        <button
          data-testid="product-views-dropdown-trigger"
          onClick={() => setViewsOpen((o) => !o)}
          className="h-9 px-3 rounded-lg border border-gray-200 flex items-center gap-1.5 text-sm text-gray-700 hover:border-brand-300 transition-colors bg-white"
        >
          <span className="text-xs text-gray-400 mr-0.5">{t("views.view_label")}</span>
          <span className="font-medium truncate max-w-[140px]">{currentView ? viewLabel(currentView) : "—"}</span>
          {currentView?.is_system && <span className="text-[9px] uppercase tracking-wider text-gray-400 border border-gray-200 rounded px-1">SYS</span>}
          {currentView?.is_shared && <span className="text-[9px] uppercase tracking-wider text-brand-600 border border-brand-200 bg-brand-50 rounded px-1" data-testid="current-view-shared-badge">{t("views.shared_badge")}</span>}
          <ChevronDown size={13} strokeWidth={1.75} className="text-gray-400" />
        </button>
        {viewsOpen && (
          <div data-testid="product-views-dropdown" className="absolute z-40 mt-1 w-72 max-h-96 overflow-y-auto right-0 rounded-lg border border-gray-200 bg-white shadow-lg p-1.5">
            {views.map((v) => (
              <div key={v.id} className={`group flex items-center gap-2 px-2 py-1.5 rounded-md text-sm hover:bg-gray-50 ${currentView?.id === v.id ? "bg-brand-50" : ""}`}>
                <button
                  data-testid={`product-view-option-${v.name}`}
                  onClick={() => { applyView(v); setViewsOpen(false); }}
                  className="flex-1 flex items-center gap-2 text-left"
                >
                  <Check size={12} strokeWidth={2} className={currentView?.id === v.id ? "text-brand-600" : "text-transparent"} />
                  <span className="truncate">{viewLabel(v)}</span>
                  {v.is_system && <span className="text-[9px] uppercase tracking-wider text-gray-400 border border-gray-200 rounded px-1">SYS</span>}
                  {v.is_shared && <span className="text-[9px] uppercase tracking-wider text-brand-600 border border-brand-200 bg-brand-50 rounded px-1" data-testid={`product-view-shared-badge-${v.name}`}><Users size={9} className="inline mr-0.5 -mt-px" />{t("views.shared_badge")}</span>}
                </button>
                {canEditView(v) && (
                  <div className="flex items-center gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity">
                    {isAdmin && (
                      <button onClick={() => toggleShareView(v)} title={v.is_shared ? t("views.unshare") : t("views.share")} className={`w-6 h-6 flex items-center justify-center rounded hover:bg-white ${v.is_shared ? "text-brand-600" : "text-gray-400 hover:text-brand-600"}`} data-testid={`product-view-share-${v.name}`}>
                        <Share2 size={11} strokeWidth={1.75} />
                      </button>
                    )}
                    <button onClick={() => openRename(v)} title="Rename" className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-brand-600 hover:bg-white" data-testid={`product-view-rename-${v.name}`}>
                      <Pencil size={11} strokeWidth={1.75} />
                    </button>
                    <button onClick={() => deleteView(v)} title="Delete" className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-white" data-testid={`product-view-delete-${v.name}`}>
                      <Trash2 size={11} strokeWidth={1.75} />
                    </button>
                  </div>
                )}
              </div>
            ))}
            <div className="border-t border-gray-100 mt-1 pt-1 px-1 space-y-0.5">
              {editable && (
                <button
                  data-testid="product-view-save-current"
                  onClick={() => { updateCurrentView(); setViewsOpen(false); }}
                  className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-gray-700 hover:bg-gray-50"
                >
                  <Save size={13} strokeWidth={1.75} className="text-gray-400" />
                  Save changes to "{currentView.name}"
                </button>
              )}
              <button
                data-testid="product-view-save-as"
                onClick={() => { openSaveAs(); setViewsOpen(false); }}
                className="w-full flex items-center gap-2 px-2 py-1.5 rounded-md text-sm text-gray-700 hover:bg-gray-50"
              >
                <Plus size={13} strokeWidth={1.75} className="text-gray-400" />
                Save as new View
              </button>
            </div>
          </div>
        )}
      </div>

      <button
        data-testid="product-view-edit-button"
        onClick={() => setEditOpen(true)}
        title="Edit view (Columns / Filters / Detail Page)"
        className="h-9 px-3 rounded-lg border border-gray-200 flex items-center gap-1.5 text-sm text-gray-700 hover:border-brand-300 transition-colors bg-white"
      >
        <Settings size={13} strokeWidth={1.75} className="text-gray-400" />
        <span>{t("views.edit")}</span>
      </button>

      <Dialog open={editOpen} onOpenChange={handleEditOpenChange}>
        <DialogContent
          className="rounded-xl sm:max-w-3xl max-h-[85vh] overflow-hidden flex flex-col p-0"
          data-testid="product-view-edit-dialog"
        >
          <DialogHeader className="px-6 pt-5 pb-3 border-b border-gray-100">
            <DialogTitle className="flex items-center gap-2">
              {t("views.edit")}
              {currentView && (
                <span className="text-sm font-normal text-gray-500">— {currentView.name}</span>
              )}
              {currentView?.is_system && (
                <span className="text-[9px] uppercase tracking-wider text-gray-400 border border-gray-200 rounded px-1">{t("views.sys_readonly")}</span>
              )}
              {currentView?.is_shared && !editable && (
                <span className="text-[9px] uppercase tracking-wider text-brand-600 border border-brand-200 rounded px-1">{t("views.shared_readonly")}</span>
              )}
            </DialogTitle>
          </DialogHeader>

          <div className="flex flex-1 overflow-hidden">
            {/* Left sidebar tabs */}
            <div className="w-40 shrink-0 border-r border-gray-100 py-3 px-2 bg-gray-50/50">
              <nav className="space-y-1">
                <TabButton icon={ColumnsIcon} active={editTab === "columns"} onClick={() => setEditTab("columns")} count={visibleColumns.length} testId="edit-tab-columns">{t("views.tab_columns")}</TabButton>
                <TabButton icon={SlidersHorizontal} active={editTab === "filters"} onClick={() => setEditTab("filters")} count={activeFilterCount} testId="edit-tab-filters">{t("views.tab_filters")}</TabButton>
                <TabButton icon={LayoutList} active={editTab === "detail"} onClick={() => setEditTab("detail")} testId="edit-tab-detail">{t("views.tab_detail")}</TabButton>
              </nav>
            </div>

            {/* Right panel */}
            <div className="flex-1 overflow-y-auto px-6 py-5">
              {editTab === "columns" && (
                <ColumnsPanel visibleColumns={visibleColumns} toggleCol={toggleCol} moveCol={moveCol} />
              )}
              {editTab === "filters" && (
                <FiltersPanel
                  visibleFilters={visibleFilters}
                  toggleFilter={toggleFilter}
                  presets={presets}
                  setPresets={(next) => { dirtyRef.current = true; setPresets(next); }}
                  categoryOptions={categoryOptions}
                  qFields={qFields}
                  toggleQField={toggleQField}
                />
              )}
              {editTab === "detail" && (
                <DetailModePanel detailView={detailView} setDetailView={(v) => { dirtyRef.current = true; setDetailView(v); }} />
              )}
            </div>
          </div>

          <DialogFooter className="border-t border-gray-100 px-6 py-3 bg-gray-50/40">
            <p className="text-xs text-gray-400 mr-auto self-center">
              {editable
                ? t("views.save_hint_editable")
                : t("views.save_hint_readonly")}
            </p>
            {!editable ? (
              <Button
                data-testid="product-view-edit-save-as"
                onClick={() => { openSaveAs(); }}
                className="rounded-lg bg-[#1677ff] hover:bg-[#0958d9]"
              >
                <Save size={13} strokeWidth={1.75} className="mr-1.5" />
                {t("views.save_as_new")}
              </Button>
            ) : (
              <Button
                data-testid="product-view-edit-save"
                onClick={() => {
                  if (!currentView) return;
                  updateCurrentView();
                  dirtyRef.current = false;
                }}
                className="rounded-lg bg-[#1677ff] hover:bg-[#0958d9]"
              >
                <Save size={13} strokeWidth={1.75} className="mr-1.5" />
                {t("views.save")}
              </Button>
            )}
            <Button variant="outline" onClick={() => setEditOpen(false)} className="rounded-lg" data-testid="product-view-edit-close">{t("views.close")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={saveDialogOpen} onOpenChange={setSaveDialogOpen}>
        <DialogContent className="rounded-xl sm:max-w-sm" data-testid="save-view-dialog">
          <DialogHeader>
            <DialogTitle>{t("views.save_as_new_title")}</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-xs font-medium text-gray-500">{t("views.view_name")}</Label>
            <Input
              data-testid="save-view-name-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              placeholder=""
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && confirmSaveAs()}
            />
            {isAdmin && (
              <label className="mt-3 flex items-start gap-2 cursor-pointer" data-testid="save-view-shared-toggle">
                <Checkbox checked={saveShared} onCheckedChange={(c) => setSaveShared(!!c)} className="mt-0.5" data-testid="save-view-shared-checkbox" />
                <span>
                  <span className="text-sm text-gray-800 flex items-center gap-1"><Users size={13} className="text-brand-600" />{t("views.share_with_all")}</span>
                  <span className="block text-xs text-gray-400">{t("views.share_with_all_hint")}</span>
                </span>
              </label>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSaveDialogOpen(false)} className="rounded-lg">{t("common.cancel")}</Button>
            <Button data-testid="save-view-confirm" onClick={confirmSaveAs} className="rounded-lg bg-[#1677ff] hover:bg-[#0958d9]">{t("common.save")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={!!renameTarget} onOpenChange={(o) => !o && setRenameTarget(null)}>
        <DialogContent className="rounded-xl sm:max-w-sm" data-testid="rename-view-dialog">
          <DialogHeader>
            <DialogTitle>{t("views.rename_title")}</DialogTitle>
          </DialogHeader>
          <div>
            <Label className="text-xs font-medium text-gray-500">{t("views.new_name")}</Label>
            <Input
              data-testid="rename-view-name-input"
              value={newName}
              onChange={(e) => setNewName(e.target.value)}
              className="mt-1.5 rounded-lg border-gray-300 focus-visible:ring-brand-500"
              autoFocus
              onKeyDown={(e) => e.key === "Enter" && confirmRename()}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setRenameTarget(null)} className="rounded-lg">{t("common.cancel")}</Button>
            <Button data-testid="rename-view-confirm" onClick={confirmRename} className="rounded-lg bg-[#1677ff] hover:bg-[#0958d9]">{t("common.rename")}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}


// ---- Small internal components for the unified Edit View dialog ----

function TabButton({ icon: Icon, active, onClick, children, count, testId }) {
  return (
    <button
      data-testid={testId}
      onClick={onClick}
      className={`w-full flex items-center gap-2 px-2.5 py-1.5 rounded-md text-sm text-left transition-colors ${active ? "bg-white text-brand-700 shadow-sm border border-gray-200" : "text-gray-600 hover:bg-white/60"}`}
    >
      <Icon size={13} strokeWidth={1.75} className={active ? "text-brand-600" : "text-gray-400"} />
      <span className="flex-1">{children}</span>
      {typeof count === "number" && (
        <span className="text-xs font-mono text-gray-400">{count}</span>
      )}
    </button>
  );
}

function ColumnsPanel({ visibleColumns, toggleCol, moveCol }) {
  const { t } = useTranslation();
  // iter 210 (2026-02): checkbox / actions 是系统列,由 ProductTable 强制
  // 渲染在首尾。这里从可编辑列表里剔除,避免用户误关后失去批量选择/操作。
  const SYSTEM_COLS = new Set(["checkbox", "actions"]);
  const editable = visibleColumns.filter((id) => !SYSTEM_COLS.has(id));
  const labelFor = (col) => {
    const key = `dashboard.columns_labels.${col.id}`;
    const v = t(key);
    return v === key ? (col.label || "") : v;
  };
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider text-gray-400 mb-2">{t("views.visible_columns")} ({editable.length})</p>
      <ul className="space-y-1">
        {editable.map((id, idx) => {
          const col = ALL_PRODUCT_COLUMNS.find((c) => c.id === id);
          if (!col) return null;
          return (
            <li key={id} className="flex items-center gap-2 border border-gray-100 rounded-md px-2 py-1.5 bg-white">
              <Checkbox checked onCheckedChange={() => toggleCol(id)} data-testid={`col-toggle-${id}`} />
              <span className="flex-1 text-sm text-gray-700 truncate">{labelFor(col) || <span className="italic text-gray-400">(select)</span>}</span>
              <button onClick={() => moveCol(id, -1)} disabled={idx === 0} className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-brand-600 hover:bg-gray-50 disabled:opacity-30 disabled:pointer-events-none" data-testid={`col-up-${id}`}>
                <ArrowUp size={11} strokeWidth={2} />
              </button>
              <button onClick={() => moveCol(id, 1)} disabled={idx === editable.length - 1} className="w-6 h-6 flex items-center justify-center rounded text-gray-400 hover:text-brand-600 hover:bg-gray-50 disabled:opacity-30 disabled:pointer-events-none" data-testid={`col-down-${id}`}>
                <ArrowDown size={11} strokeWidth={2} />
              </button>
            </li>
          );
        })}
      </ul>
      <p className="text-[10px] uppercase tracking-wider text-gray-400 mt-4 mb-2">{t("common.actions") === "操作" ? "可选" : "Available"}</p>
      <ul className="space-y-1">
        {ALL_PRODUCT_COLUMNS
          .filter((c) => !SYSTEM_COLS.has(c.id) && !visibleColumns.includes(c.id))
          .sort((a, b) => labelFor(a).localeCompare(labelFor(b), "zh-Hans-CN"))
          .map((col) => (
          <li key={col.id} className="flex items-center gap-2 border border-transparent rounded-md px-2 py-1.5 hover:bg-gray-50">
            <Checkbox checked={false} onCheckedChange={() => toggleCol(col.id)} data-testid={`col-toggle-${col.id}`} />
            <span className="flex-1 text-sm text-gray-500 truncate">{labelFor(col) || <span className="italic text-gray-400">(select)</span>}</span>
          </li>
        ))}
      </ul>
      <p className="text-[10px] text-gray-400 mt-3 leading-snug">选择框 / 操作 列为系统固定列,始终显示,不可关闭。</p>
    </div>
  );
}

function FiltersPanel({ visibleFilters, toggleFilter, presets, setPresets, categoryOptions, qFields, toggleQField }) {
  const { t } = useTranslation();
  const filterLabelFor = (key) => {
    // iter 178w: FilterBar's FILTER_LABELS are English fallbacks; look up
    // dashboard.filters.<key> first, then fall back so any key not yet
    // translated still renders sensibly.
    const map = {
      keyword: "dashboard.filters.keyword_search",
      customer: "dashboard.filters.customer",
      budget_no: "dashboard.filters.budget_no",
      factory_name: "dashboard.filters.factory",
      purchaser: "dashboard.filters.purchaser",
      external_sales: "dashboard.filters.external_sales",
      contract_no: "dashboard.filters.contract_no",
      invoice_no: "dashboard.filters.invoice_no",
      tag: "dashboard.filters.tag",
      category: "dashboard.filters.category",
      product_type: "dashboard.filters.product_type",
      approval: "dashboard.filters.approval",
      qty: "dashboard.filters.quantity_range",
      shipping_qty: "dashboard.filters.shipping_qty_range",
      ci_qty: "dashboard.filters.ci_qty_range",
      has_samples: "dashboard.filters.has_samples",
      sample_warehouse: "dashboard.filters.sample_warehouse",
      date: "dashboard.filters.date_range",
    };
    const tKey = map[key];
    if (!tKey) return FILTER_LABELS[key] || key;
    const v = t(tKey);
    return v === tKey ? (FILTER_LABELS[key] || key) : v;
  };
  return (
    <div>
      {/* iter 201: 搜索框范围 —— 每个 View 独立配置。取消勾选即从 OR 查询里剔除。
          默认锁定 5 个业务最常用字段;其它可选字段(item_no/invoice_no/po_number)
          按需勾上。列表由 Q_FIELD_CATALOG 定义。 */}
      <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">Search Box Scope · 搜索框范围</p>
      <p className="text-[11px] text-gray-500 mb-2 leading-snug">Toolbar 上"关键词搜索"输入框会 OR-匹配这里勾选的字段。</p>
      <ul className="space-y-1 mb-6">
        {Q_FIELD_CATALOG.map((f) => {
          const currentList = Array.isArray(qFields) && qFields.length ? qFields : DEFAULT_Q_FIELDS;
          const checked = currentList.includes(f.id);
          return (
            <li key={f.id} className="flex items-center gap-2 border border-gray-100 rounded-md px-2 py-1.5">
              <Checkbox checked={checked} onCheckedChange={() => toggleQField(f.id)} data-testid={`qfield-toggle-${f.id}`} />
              <span className="text-sm text-gray-700">{f.label}</span>
            </li>
          );
        })}
      </ul>

      <p className="text-[11px] uppercase tracking-wider text-gray-400 mb-2">{t("views.visible_filters")}</p>
      <ul className="space-y-1">
        {ALL_FILTER_KEYS.map((key) => {
          const checked = !visibleFilters || visibleFilters.length === 0 || visibleFilters.includes(key);
          return (
            <li key={key} className="flex items-center gap-2 border border-gray-100 rounded-md px-2 py-1.5">
              <Checkbox checked={checked} onCheckedChange={() => toggleFilter(key)} data-testid={`filter-toggle-${key}`} />
              <span className="text-sm text-gray-700">{filterLabelFor(key)}</span>
            </li>
          );
        })}
      </ul>

      <p className="text-[11px] uppercase tracking-wider text-gray-400 mt-6 mb-2">{t("views.presets_title")}</p>
      <PresetsPanel presets={presets || {}} setPresets={setPresets} categoryOptions={categoryOptions} />
    </div>
  );
}

function PresetsPanel({ presets, setPresets, categoryOptions }) {
  const update = (fieldId, patch) => {
    setPresets({ ...presets, [fieldId]: { ...(presets[fieldId] || emptyPreset(fieldId)), ...patch } });
  };
  const clear = (fieldId) => {
    const next = { ...presets };
    delete next[fieldId];
    setPresets(next);
  };

  return (
    <div className="space-y-1.5" data-testid="preset-values-panel">
      {PRESET_FIELDS.map((cfg) => {
        const preset = presets[cfg.id];
        const active = !!preset && preset.mode && preset.mode !== "none";
        return (
          <div key={cfg.id} className={`grid grid-cols-[140px_120px_1fr_28px] gap-2 items-center border rounded-md px-2 py-1.5 ${active ? "border-brand-200 bg-brand-50/30" : "border-gray-100"}`}>
            <span className="text-xs text-gray-700 truncate" title={cfg.label}>{cfg.label}</span>
            <select
              data-testid={`preset-mode-${cfg.id}`}
              value={preset?.mode || "none"}
              onChange={(e) => {
                const mode = e.target.value;
                if (mode === "none") clear(cfg.id);
                else update(cfg.id, { mode });
              }}
              className="h-8 rounded-md border border-gray-200 text-xs px-2 focus:outline-none focus:border-brand-500 bg-white"
            >
              <option value="none">(none)</option>
              {cfg.modes.map((m) => (
                <option key={m.v} value={m.v}>{m.label}</option>
              ))}
            </select>
            <PresetValueInput
              cfg={cfg}
              preset={preset}
              onChange={(patch) => update(cfg.id, patch)}
              disabled={!active}
              categoryOptions={categoryOptions}
            />
            <button
              type="button"
              onClick={() => clear(cfg.id)}
              disabled={!active}
              className="w-7 h-7 flex items-center justify-center rounded text-gray-400 hover:text-red-600 hover:bg-white disabled:opacity-30 disabled:pointer-events-none"
              title="Clear preset"
              data-testid={`preset-clear-${cfg.id}`}
            >
              <Trash2 size={12} strokeWidth={1.75} />
            </button>
          </div>
        );
      })}
    </div>
  );
}

function PresetValueInput({ cfg, preset, onChange, disabled, categoryOptions }) {
  const value = preset?.value ?? "";
  const value2 = preset?.value2 ?? "";
  const dateField = preset?.dateField ?? "contract_date";
  const cx = "h-8 rounded-md border border-gray-200 text-xs px-2 bg-white focus:outline-none focus:border-brand-500 disabled:bg-gray-50 disabled:text-gray-400";

  if (cfg.type === "text") {
    return <input data-testid={`preset-value-${cfg.id}`} type="text" value={value} disabled={disabled} onChange={(e) => onChange({ value: e.target.value })} className={cx} placeholder={disabled ? "—" : "e.g. CI"} />;
  }
  if (cfg.type === "select-category") {
    return (
      <select data-testid={`preset-value-${cfg.id}`} value={value} disabled={disabled} onChange={(e) => onChange({ value: e.target.value })} className={cx}>
        <option value="">—</option>
        {(categoryOptions || []).map((c) => <option key={c} value={c}>{c}</option>)}
      </select>
    );
  }
  if (cfg.type === "select-product-type") {
    return (
      <select data-testid={`preset-value-${cfg.id}`} value={value} disabled={disabled} onChange={(e) => onChange({ value: e.target.value })} className={cx}>
        <option value="">—</option>
        <option value="CI产品">CI Product</option>
        <option value="拼箱产品">Consolidated Shipment</option>
      </select>
    );
  }
  if (cfg.type === "select-bool") {
    return (
      <select data-testid={`preset-value-${cfg.id}`} value={value} disabled={disabled} onChange={(e) => onChange({ value: e.target.value })} className={cx}>
        <option value="">—</option>
        <option value="true">Yes</option>
      </select>
    );
  }
  if (cfg.type === "select-approval") {
    return (
      <select data-testid={`preset-value-${cfg.id}`} value={value} disabled={disabled} onChange={(e) => onChange({ value: e.target.value })} className={cx}>
        <option value="">—</option>
        <option value="yes">Approved</option>
        <option value="no">Rejected</option>
        <option value="empty">Pending</option>
      </select>
    );
  }
  if (cfg.type === "qty_range") {
    return (
      <div className="flex items-center gap-1.5">
        <input data-testid={`preset-value-${cfg.id}-min`} type="number" value={value} disabled={disabled} onChange={(e) => onChange({ value: e.target.value })} className={`${cx} flex-1 font-mono`} placeholder="Min" />
        <span className="text-gray-300 text-xs">–</span>
        <input data-testid={`preset-value-${cfg.id}-max`} type="number" value={value2} disabled={disabled} onChange={(e) => onChange({ value2: e.target.value })} className={`${cx} flex-1 font-mono`} placeholder="Max" />
      </div>
    );
  }
  if (cfg.type === "date_range") {
    return (
      <div className="flex items-center gap-1.5">
        <select data-testid={`preset-value-${cfg.id}-field`} value={dateField} disabled={disabled} onChange={(e) => onChange({ dateField: e.target.value })} className={`${cx} w-28`}>
          <option value="contract_date">Contract Date</option>
          <option value="delivery_date">Delivery Date</option>
          <option value="shipping_date">Shipping Date</option>
          <option value="po_shipping_date">PO Shipping Date</option>
        </select>
        <input data-testid={`preset-value-${cfg.id}-from`} type="date" value={value} disabled={disabled} onChange={(e) => onChange({ value: e.target.value })} className={`${cx} flex-1 font-mono`} />
        <input data-testid={`preset-value-${cfg.id}-to`} type="date" value={value2} disabled={disabled} onChange={(e) => onChange({ value2: e.target.value })} className={`${cx} flex-1 font-mono`} />
      </div>
    );
  }
  return null;
}

function DetailModePanel({ detailView, setDetailView }) {
  const { t } = useTranslation();
  const options = [
    { id: "pooled", labelKey: "views.detail_pooled_label", hintKey: "views.detail_pooled_hint" },
    { id: "order", labelKey: "views.detail_order_label", hintKey: "views.detail_order_hint" },
  ];
  return (
    <div>
      <p className="text-xs text-gray-500 mb-3">{t("views.detail_mode_hint")}</p>
      <div className="grid grid-cols-2 gap-3">
        {options.map((o) => (
          <button
            key={o.id}
            data-testid={`detail-mode-${o.id}`}
            onClick={() => setDetailView(o.id)}
            className={`text-left rounded-lg border p-3 transition-colors ${detailView === o.id ? "border-brand-500 bg-brand-50/40 ring-1 ring-brand-500/30" : "border-gray-200 bg-white hover:border-gray-300"}`}
          >
            <div className="flex items-center justify-between mb-1">
              <span className="text-sm font-medium text-gray-800">{t(o.labelKey)}</span>
              {detailView === o.id && <Check size={14} strokeWidth={2} className="text-brand-600" />}
            </div>
            <p className="text-xs text-gray-500 leading-relaxed">{t(o.hintKey)}</p>
          </button>
        ))}
      </div>
    </div>
  );
}
